import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Wrench, Calendar, Package, FileText, Search, X, ClipboardList, CheckCircle2, 
  Award, Download, LogOut, Users, Shield, FileSpreadsheet, AlertTriangle, 
  Gauge, Factory, FilePlus, FileBadge2, Database, LayoutDashboard, Building2, ClipboardCheck, Edit,
  BookOpen, Network, AlertOctagon, ChevronLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import CertificateOfConformance from '@/components/CertificateOfConformance';
import { useAuth } from '@/context/AuthContext';
import NcrKanbanBoard from '@/components/NcrKanbanBoard';
import PolicyManager from '@/components/PolicyManager';
import ProcedureManager from '@/components/ProcedureManager';
import CustomerManagement from '@/components/CustomerManagement';
import ActionItemsPanel from '@/components/ActionItemsPanel';
import RiskManager from '@/components/RiskManager';
import MaintenanceModule from '@/components/MaintenanceModule';
import { ReactorCore, OrbitalNode, ConnectionLine, ProductionSubmenu, StatusBar } from '@/components/RadialMenuComponents';
import { formatDate } from '@/utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============ MAIN LANDING COMPONENT ============

const Landing = () => {
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  
  // Stats state
  const [stats, setStats] = useState({
    activeOrders: 0,
    qualityAlerts: 0,
    nextDelivery: null,
    nextDeliveryDetails: null,
    openNcrs: 0,
    missingCerts: 0,
    calibrationsDue: 0,
    lateOrders: 0
  });
  
  // Production submenu state
  const [showProductionSubmenu, setShowProductionSubmenu] = useState(false);
  
  // Dialog states (preserved from legacy)
  const [showNcrModule, setShowNcrModule] = useState(false);
  const [showPolicyModule, setShowPolicyModule] = useState(false);
  const [showProcedureModule, setShowProcedureModule] = useState(false);
  const [showQualityManual, setShowQualityManual] = useState(false);
  const [showRiskManager, setShowRiskManager] = useState(false);
  const [showOrganogram, setShowOrganogram] = useState(false);
  const [showMaintenance, setShowMaintenance] = useState(false);
  const [OrgComp, setOrgComp] = useState(null);
  const [showIsoSubmenu, setShowIsoSubmenu] = useState(false);
  const [ncrs, setNcrs] = useState([]);
  const [showOutstandingOrders, setShowOutstandingOrders] = useState(false);
  const [outstandingOrders, setOutstandingOrders] = useState([]);
  const [loadingOutstanding, setLoadingOutstanding] = useState(false);
  const [outstandingFilterCustomer, setOutstandingFilterCustomer] = useState('');
  const [outstandingFilterPart, setOutstandingFilterPart] = useState('');
  const [uniqueCustomers, setUniqueCustomers] = useState([]);
  const [uniqueParts, setUniqueParts] = useState([]);
  const [showCompletedOrders, setShowCompletedOrders] = useState(false);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [loadingCompleted, setLoadingCompleted] = useState(false);
  const [completedFilterCustomer, setCompletedFilterCustomer] = useState('');
  const [completedFilterPart, setCompletedFilterPart] = useState('');
  const [completedUniqueCustomers, setCompletedUniqueCustomers] = useState([]);
  const [completedUniqueParts, setCompletedUniqueParts] = useState([]);
  const [showExtractDialog, setShowExtractDialog] = useState(false);
  const [extractOrders, setExtractOrders] = useState([]);
  const [loadingExtract, setLoadingExtract] = useState(false);
  const [extractSearchQuery, setExtractSearchQuery] = useState('');
  const [extractingOrder, setExtractingOrder] = useState(null);
  const [parts, setParts] = useState([]);
  const [loadingBackup, setLoadingBackup] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);
  const [certificateOrder, setCertificateOrder] = useState(null);
  const [certificatePartDetails, setCertificatePartDetails] = useState(null);
  const [showCustomerManagement, setShowCustomerManagement] = useState(false);
  const [showActionItems, setShowActionItems] = useState(false);
  const [mostAdvancedOrders, setMostAdvancedOrders] = useState([]);

  useEffect(() => {
    fetchStats();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStats = async () => {
    try {
      const [ordersRes, ncrsRes, equipmentRes, certsRes, partsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/works-orders`),
        axios.get(`${BACKEND_URL}/api/ncrs`),
        axios.get(`${BACKEND_URL}/api/equipment/stats`),
        axios.get(`${BACKEND_URL}/api/certificates/stats`),
        axios.get(`${BACKEND_URL}/api/parts`)
      ]);

      const orders = ordersRes.data;
      const ncrsData = ncrsRes.data;
      const partsData = partsRes.data;
      setNcrs(ncrsData);
      setParts(partsData);

      // Calculate active orders (not closed — all processes including Invoice must be Complete to be closed)
      const activeOrders = orders.filter(order => {
        if (order.current_status === 'Closed') return false;
        const seq = order.process_sequence;
        if (seq.length === 0) return true;
        const allDone = seq.every((p, idx) => {
          const status = order.process_statuses[String(idx)] || order.process_statuses[p];
          return status === 'Complete';
        });
        return !allDone;
      });

      // Calculate late orders
      const now = new Date();
      const lateOrders = activeOrders.filter(order => {
        const dueDate = new Date(order.due_date);
        return dueDate < now;
      });

      // Find next delivery with details
      const sortedOrders = [...activeOrders].sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
      const nextOrder = sortedOrders[0];
      const nextDelivery = nextOrder?.order_number || null;
      
      // Build next delivery details
      let nextDeliveryDetails = null;
      if (nextOrder) {
        const part = partsData.find(p => p.part_number === nextOrder.part_number);
        const dueDate = new Date(nextOrder.due_date);
        const daysUntilDue = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
        nextDeliveryDetails = {
          orderNumber: nextOrder.order_number,
          partNumber: nextOrder.part_number,
          description: part?.description || nextOrder.part_number,
          customer: nextOrder.customer,
          dueDate: nextOrder.due_date,
          daysUntilDue,
          isOverdue: daysUntilDue < 0
        };
      }

      // Open NCRs
      const openNcrs = ncrsData.filter(n => n.status !== 'Closed').length;

      // Equipment stats
      const calibrationsDue = (equipmentRes.data.due_soon || 0) + (equipmentRes.data.overdue || 0);

      // Certificate stats
      const missingCerts = certsRes.data.pending_review || 0;

      // Calculate most advanced orders (closest to completion)
      const ordersWithProgress = activeOrders.map(order => {
        let completedSteps = 0;
        const totalSteps = order.process_sequence.length;
        let currentProcess = '';
        
        for (let i = 0; i < totalSteps; i++) {
          const status = order.process_statuses[String(i)] || order.process_statuses[order.process_sequence[i]];
          if (status === 'Complete') {
            completedSteps++;
          } else if (!currentProcess) {
            currentProcess = order.process_sequence[i];
          }
        }
        
        const progressPercent = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;
        const part = partsData.find(p => p.part_number === order.part_number);
        
        return {
          order_number: order.order_number,
          customer: order.customer,
          part_number: order.part_number,
          description: part?.description || order.part_number,
          quantity: order.quantity,
          due_date: order.due_date,
          current_process: currentProcess || order.process_sequence[totalSteps - 1],
          completed_steps: completedSteps,
          total_steps: totalSteps,
          progress_percent: progressPercent
        };
      });
      
      // Sort by progress (highest first) and take top 5
      const topAdvanced = ordersWithProgress
        .sort((a, b) => b.progress_percent - a.progress_percent)
        .slice(0, 5);
      
      setMostAdvancedOrders(topAdvanced);

      setStats({
        activeOrders: activeOrders.length,
        qualityAlerts: openNcrs,
        nextDelivery,
        nextDeliveryDetails,
        openNcrs,
        missingCerts,
        calibrationsDue,
        lateOrders: lateOrders.length
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Helper functions (preserved from legacy)
  const getOrderProcessStatus = (order, index, processName) => {
    if (order.process_statuses[String(index)] !== undefined) {
      return order.process_statuses[String(index)];
    }
    return order.process_statuses[processName] || 'Not Ready';
  };

  const getCurrentProcessInfo = (order) => {
    for (let i = 0; i < order.process_sequence.length; i++) {
      const process = order.process_sequence[i];
      const status = getOrderProcessStatus(order, i, process);
      if (status !== 'Complete') {
        return { process, status: status || 'Not Ready' };
      }
    }
    const lastProcess = order.process_sequence[order.process_sequence.length - 1];
    const lastIdx = order.process_sequence.length - 1;
    return { process: lastProcess, status: getOrderProcessStatus(order, lastIdx, lastProcess) || 'Complete' };
  };

  const getPartDescription = (partNumber) => {
    const part = parts.find(p => p.part_number === partNumber);
    return part?.description || '';
  };

  // Fetch functions (preserved from legacy)
  const fetchOutstandingOrders = async () => {
    setLoadingOutstanding(true);
    try {
      const [ordersResponse, partsResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/works-orders`),
        axios.get(`${BACKEND_URL}/api/parts`)
      ]);
      
      setParts(partsResponse.data);
      
      const outstanding = ordersResponse.data.filter(order => {
        if (order.current_status === 'Closed') return false;
        const seq = order.process_sequence;
        if (seq.length === 0) return true;
        const allDone = seq.every((p, idx) => {
          const status = order.process_statuses[String(idx)] || order.process_statuses[p];
          return status === 'Complete';
        });
        return !allDone;
      });
      
      outstanding.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
      
      setOutstandingOrders(outstanding);
      const customers = [...new Set(outstanding.map(o => o.customer))].sort();
      const partNumbers = [...new Set(outstanding.map(o => o.part_number))].sort();
      setUniqueCustomers(customers);
      setUniqueParts(partNumbers);
      setShowOutstandingOrders(true);
      
      if (outstanding.length === 0) {
        toast.info('No outstanding orders found');
      }
    } catch (error) {
      console.error('Error fetching outstanding orders:', error);
      toast.error('Failed to fetch outstanding orders');
    } finally {
      setLoadingOutstanding(false);
    }
  };

  const fetchCompletedOrders = async () => {
    setLoadingCompleted(true);
    try {
      const [ordersResponse, partsResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/works-orders`),
        axios.get(`${BACKEND_URL}/api/parts`)
      ]);
      
      setParts(partsResponse.data);
      
      const completed = ordersResponse.data.filter(order => {
        if (order.current_status === 'Closed') return true;
        const seq = order.process_sequence;
        if (seq.length === 0) return false;
        return seq.every((p, idx) => {
          const status = order.process_statuses[String(idx)] || order.process_statuses[p];
          return status === 'Complete';
        });
      });
      
      completed.sort((a, b) => {
        const dateA = new Date(a.completed_at || a.created_at || 0);
        const dateB = new Date(b.completed_at || b.created_at || 0);
        return dateB - dateA;
      });
      
      setCompletedOrders(completed);
      const customers = [...new Set(completed.map(o => o.customer))].sort();
      const partNumbers = [...new Set(completed.map(o => o.part_number))].sort();
      setCompletedUniqueCustomers(customers);
      setCompletedUniqueParts(partNumbers);
      setShowCompletedOrders(true);
      
      if (completed.length === 0) {
        toast.info('No completed orders found');
      }
    } catch (error) {
      console.error('Error fetching completed orders:', error);
      toast.error('Failed to fetch completed orders');
    } finally {
      setLoadingCompleted(false);
    }
  };

  const fetchExtractOrders = async () => {
    setLoadingExtract(true);
    try {
      const [ordersResponse, partsResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/works-orders`),
        axios.get(`${BACKEND_URL}/api/parts`)
      ]);
      
      setParts(partsResponse.data);
      
      const completed = ordersResponse.data.filter(order => {
        if (order.current_status === 'Closed') return true;
        const seq = order.process_sequence;
        if (seq.length === 0) return false;
        return seq.every((p, idx) => {
          const status = order.process_statuses[String(idx)] || order.process_statuses[p];
          return status === 'Complete';
        });
      });
      
      completed.sort((a, b) => {
        const dateA = new Date(a.completed_at || a.created_at || 0);
        const dateB = new Date(b.completed_at || b.created_at || 0);
        return dateB - dateA;
      });
      
      setExtractOrders(completed);
      setShowExtractDialog(true);
    } catch (error) {
      console.error('Error fetching orders for extraction:', error);
      toast.error('Failed to fetch completed orders');
    } finally {
      setLoadingExtract(false);
    }
  };

  const handleBackupData = async () => {
    setLoadingBackup(true);
    try {
      // Fetch all data collections in parallel
      const [partsRes, ordersRes, inventoryRes, ncrsRes, equipmentRes, certificatesRes, policiesRes, proceduresRes, customersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/parts`),
        axios.get(`${BACKEND_URL}/api/works-orders`),
        axios.get(`${BACKEND_URL}/api/inventory`),
        axios.get(`${BACKEND_URL}/api/ncrs`),
        axios.get(`${BACKEND_URL}/api/equipment`),
        axios.get(`${BACKEND_URL}/api/certificates`),
        axios.get(`${BACKEND_URL}/api/policies`),
        axios.get(`${BACKEND_URL}/api/procedures`),
        axios.get(`${BACKEND_URL}/api/customers`)
      ]);

      const workbook = XLSX.utils.book_new();

      // Parts Sheet
      const partsData = partsRes.data.map(part => ({
        'Part Number': part.part_number,
        'Drawing Number': part.drawing_number,
        'Description': part.description,
        'Material Grade': part.material_grade,
        'Diameter (mm)': part.diameter,
        'Length of Cut (mm)': part.loc,
        'Heat Treatment': part.heat_treatment,
        'Process Sequence': part.process_sequence?.join(' → ') || ''
      }));
      const partsSheet = XLSX.utils.json_to_sheet(partsData);
      XLSX.utils.book_append_sheet(workbook, partsSheet, 'Parts');

      // Works Orders Sheet
      const ordersData = ordersRes.data.map(order => ({
        'Order Number': order.order_number,
        'Customer': order.customer,
        'Customer Order': order.customer_order_number || '',
        'Part Number': order.part_number,
        'Quantity': order.quantity,
        'Unit Price': order.unit_price || 0,
        'Order Value': (order.quantity || 0) * (order.unit_price || 0),
        'Due Date': formatDate(order.due_date),
        'Created Date': formatDate(order.created_at),
        'Process Sequence': order.process_sequence?.join(' → ') || '',
        'Notes': order.notes || ''
      }));
      const ordersSheet = XLSX.utils.json_to_sheet(ordersData);
      XLSX.utils.book_append_sheet(workbook, ordersSheet, 'Works Orders');

      // Inventory Sheet
      const inventoryData = inventoryRes.data.map(item => ({
        'Material Grade': item.material_grade,
        'Diameter (mm)': item.diameter,
        'Heat Treatment Condition': item.heat_treatment_condition,
        'Quantity Available': item.quantity_available,
        'Unit': item.unit || 'bars'
      }));
      const inventorySheet = XLSX.utils.json_to_sheet(inventoryData);
      XLSX.utils.book_append_sheet(workbook, inventorySheet, 'Inventory');

      // Customers Sheet
      const customersData = customersRes.data.map(cust => ({
        'Customer ID': cust.customer_id,
        'Name': cust.name,
        'Contact Person': cust.contact_person || '',
        'Email': cust.email || '',
        'Phone': cust.phone || '',
        'Address': cust.address || '',
        'City': cust.city || '',
        'Country': cust.country || '',
        'Payment Terms': cust.payment_terms || '',
        'Status': cust.is_active ? 'Active' : 'Inactive',
        'Notes': cust.notes || '',
        'Created Date': formatDate(cust.created_at)
      }));
      const customersSheet = XLSX.utils.json_to_sheet(customersData);
      XLSX.utils.book_append_sheet(workbook, customersSheet, 'Customers');

      // NCR Sheet
      const ncrsData = ncrsRes.data.map(ncr => ({
        'NCR Number': ncr.ncr_number,
        'Type': ncr.ncr_type,
        'Source': ncr.source,
        'Severity': ncr.severity,
        'Status': ncr.status,
        'Department': ncr.department || '',
        'Description': ncr.description,
        'Work Order Reference': ncr.work_order_reference || '',
        'Customer Reference': ncr.customer_reference || '',
        'Raised By': ncr.raised_by,
        'Raised Date': formatDate(ncr.date_raised),
        'Due Date': ncr.due_date ? formatDate(ncr.due_date) : '',
        'Responsible Person': ncr.responsible_person || '',
        'Containment Action': ncr.containment?.action || '',
        'Root Cause': ncr.five_why?.root_cause || '',
        'Corrective Action': ncr.corrective_action?.action || '',
        'Closed By': ncr.closed_by || '',
        'Closure Date': ncr.closure_date ? formatDate(ncr.closure_date) : '',
        'Closure Notes': ncr.closure_notes || ''
      }));
      const ncrsSheet = XLSX.utils.json_to_sheet(ncrsData);
      XLSX.utils.book_append_sheet(workbook, ncrsSheet, 'NCRs');

      // Equipment/Calibration Sheet
      const equipmentData = equipmentRes.data.map(eq => ({
        'Equipment ID': eq.equipment_id,
        'Name': eq.name,
        'Category': eq.category,
        'Serial Number': eq.serial_number || '',
        'Manufacturer': eq.manufacturer || '',
        'Model': eq.model || '',
        'Range': eq.range_spec || '',
        'Resolution': eq.resolution || '',
        'Accuracy': eq.accuracy || '',
        'Status': eq.status,
        'Criticality': eq.criticality,
        'Department': eq.department || '',
        'Custodian': eq.custodian || '',
        'Location': eq.storage_location || '',
        'Calibration Frequency': eq.calibration_frequency,
        'Calibration Method': eq.calibration_method,
        'Last Calibration': eq.last_calibration_date ? formatDate(eq.last_calibration_date) : '',
        'Next Calibration Due': eq.next_calibration_due ? formatDate(eq.next_calibration_due) : '',
        'Certificate Number': eq.certificate_number || ''
      }));
      const equipmentSheet = XLSX.utils.json_to_sheet(equipmentData);
      XLSX.utils.book_append_sheet(workbook, equipmentSheet, 'Equipment');

      // Certificates Sheet
      const certificatesData = certificatesRes.data.map(cert => ({
        'Certificate ID': cert.certificate_id,
        'Type': cert.certificate_type,
        'Title': cert.title,
        'Description': cert.description || '',
        'Status': cert.status,
        'Supplier': cert.supplier || '',
        'Customer': cert.customer || '',
        'Material Grade': cert.material_grade || '',
        'Batch/Heat Number': cert.batch_heat_number || '',
        'Issue Date': cert.issue_date ? formatDate(cert.issue_date) : '',
        'Expiry Date': cert.expiry_date ? formatDate(cert.expiry_date) : '',
        'Uploaded Date': formatDate(cert.uploaded_at),
        'Uploaded By': cert.uploaded_by || ''
      }));
      const certificatesSheet = XLSX.utils.json_to_sheet(certificatesData);
      XLSX.utils.book_append_sheet(workbook, certificatesSheet, 'Certificates');

      // Policies Sheet
      const policiesData = policiesRes.data.map(policy => ({
        'Policy ID': policy.policy_id,
        'Category': policy.category,
        'Title': policy.title,
        'Description': policy.description || '',
        'Status': policy.status,
        'Version': policy.version,
        'Effective Date': policy.effective_date ? formatDate(policy.effective_date) : '',
        'Review Date': policy.review_date ? formatDate(policy.review_date) : '',
        'Owner': policy.owner || '',
        'Approved By': policy.approved_by || '',
        'Created Date': formatDate(policy.created_at)
      }));
      const policiesSheet = XLSX.utils.json_to_sheet(policiesData);
      XLSX.utils.book_append_sheet(workbook, policiesSheet, 'Policies');

      // Procedures Sheet
      const proceduresData = proceduresRes.data.map(proc => ({
        'Procedure ID': proc.procedure_id,
        'Category': proc.category,
        'Title': proc.title,
        'Description': proc.description || '',
        'Status': proc.status,
        'Version': proc.version,
        'Effective Date': proc.effective_date ? formatDate(proc.effective_date) : '',
        'Review Date': proc.review_date ? formatDate(proc.review_date) : '',
        'Owner': proc.owner || '',
        'Approved By': proc.approved_by || '',
        'Created Date': formatDate(proc.created_at)
      }));
      const proceduresSheet = XLSX.utils.json_to_sheet(proceduresData);
      XLSX.utils.book_append_sheet(workbook, proceduresSheet, 'Procedures');

      // Generate filename with timestamp
      const today = new Date();
      const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
      const timeStr = `${String(today.getHours()).padStart(2, '0')}${String(today.getMinutes()).padStart(2, '0')}`;
      XLSX.writeFile(workbook, `Hotforge_Full_Backup_${dateStr}_${timeStr}.xlsx`);
      
      // Summary toast
      const totalRecords = partsRes.data.length + ordersRes.data.length + inventoryRes.data.length + 
                          ncrsRes.data.length + equipmentRes.data.length + certificatesRes.data.length +
                          policiesRes.data.length + proceduresRes.data.length + customersRes.data.length;
      toast.success(`Backup complete! ${totalRecords} records exported across 9 sheets.`);
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error('Failed to create backup');
    } finally {
      setLoadingBackup(false);
    }
  };

  const handleExtractWorksOrder = async (order) => {
    setExtractingOrder(order.order_number);
    try {
      const partResponse = await axios.get(`${BACKEND_URL}/api/parts/${order.part_number}`);
      const part = partResponse.data;

      const workbook = XLSX.utils.book_new();

      // Helper to get process data by index or name
      const getProcessData = (processName) => {
        const idx = order.process_sequence.indexOf(processName);
        if (idx !== -1) {
          return order.process_data?.[String(idx)] || order.process_data?.[processName] || {};
        }
        return order.process_data?.[processName] || {};
      };

      // Get data from specific processes
      const cuttingData = getProcessData('Cutting');
      const heatTreatmentData = getProcessData('Heat Treatment');
      const surfaceCoatingData = getProcessData('Surface Coating');
      const finalInspectionData = getProcessData('Final Inspection');
      const invoiceData = getProcessData('Invoice');
      const forgingData = getProcessData('Forging');
      const machiningData = getProcessData('Machining');
      const millingData = getProcessData('Milling');

      // ============ SHEET 1: SUMMARY ============
      const summaryData = [
        ['Works Order Number', order.order_number],
        ['Customer', order.customer],
        ['Customer Order Number', order.customer_order_number || ''],
        ['Order Quantity', order.quantity],
        ['Final Count Quantity', finalInspectionData.quantity || ''],
        ['Part Number', order.part_number],
        ['Part Description', part.description || ''],
        ['Material Grade', part.material_grade || ''],
        ['Cast Number', cuttingData.cast_number || ''],
        ['Bundle Number', cuttingData.bundle_number || ''],
        ['Heat Treatment Certificate Number', heatTreatmentData.ht_certificate_number || ''],
        ['Surface Coating Certificate Number', surfaceCoatingData.sc_certificate_number || ''],
        ['Delivery Method', finalInspectionData.delivery_method || invoiceData.delivery_method || ''],
        ['Invoice Number', invoiceData.invoice_number || ''],
        ['Invoice Delivery Note Number', invoiceData.delivery_note_number || ''],
        ['Invoice Date', invoiceData.invoice_date || '']
      ];
      const summarySheet = XLSX.utils.aoa_to_sheet([['Field', 'Value'], ...summaryData]);
      summarySheet['!cols'] = [{ wch: 35 }, { wch: 40 }];
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

      // ============ SHEET 2: SCRAP REPORT ============
      const forgeScrap = parseInt(forgingData.quantity_scrap) || 0;
      const machiningScrap = parseInt(machiningData.quantity_scrap) || 0;
      const millingScrap = parseInt(millingData.quantity_scrap) || 0;
      const otherScrap = parseInt(finalInspectionData.other_scrap) || 0;
      const totalScrap = forgeScrap + machiningScrap + millingScrap + otherScrap;
      const unitPrice = order.unit_price || 0;
      const totalScrapValue = totalScrap * unitPrice;

      const scrapData = [
        ['Works Order Number', order.order_number],
        ['Customer', order.customer],
        ['Customer Order Number', order.customer_order_number || ''],
        ['Order Quantity', order.quantity],
        ['Part Number', order.part_number],
        ['Part Description', part.description || ''],
        ['Material Grade', part.material_grade || ''],
        ['Forge Scrap', forgeScrap],
        ['Machining Scrap', machiningScrap],
        ['Milling Scrap', millingScrap],
        ['Other Scrap', otherScrap],
        ['Total Scrap', totalScrap],
        ['Unit Price (R)', unitPrice.toFixed(2)],
        ['Total Scrap Value (R)', totalScrapValue.toFixed(2)]
      ];
      const scrapSheet = XLSX.utils.aoa_to_sheet([['Field', 'Value'], ...scrapData]);
      scrapSheet['!cols'] = [{ wch: 30 }, { wch: 25 }];
      XLSX.utils.book_append_sheet(workbook, scrapSheet, 'Scrap Report');

      // ============ SHEET 3: PROCESS STEPS ============
      const processRows = [['Process Step', 'Status', 'Field', 'Value']];
      
      order.process_sequence.forEach((processName, idx) => {
        const status = order.process_statuses?.[String(idx)] || order.process_statuses?.[processName] || 'Not Started';
        const data = order.process_data?.[String(idx)] || order.process_data?.[processName] || {};
        
        // Add process header
        processRows.push([processName, status, '', '']);
        
        // Add all recorded data for this process
        const dataEntries = Object.entries(data);
        if (dataEntries.length > 0) {
          dataEntries.forEach(([field, value]) => {
            // Format field name for display
            const formattedField = field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            processRows.push(['', '', formattedField, value !== null && value !== undefined ? String(value) : '']);
          });
        } else {
          processRows.push(['', '', '(No data recorded)', '']);
        }
        
        // Add blank row between processes
        processRows.push(['', '', '', '']);
      });

      const processSheet = XLSX.utils.aoa_to_sheet(processRows);
      processSheet['!cols'] = [{ wch: 30 }, { wch: 15 }, { wch: 35 }, { wch: 40 }];
      XLSX.utils.book_append_sheet(workbook, processSheet, 'Process Steps');

      // ============ SHEET 4: PART DETAILS ============
      const partDetailsData = [
        ['Part Number', part.part_number],
        ['Drawing Number', part.drawing_number || ''],
        ['Description', part.description || ''],
        ['Material Grade', part.material_grade || ''],
        ['Diameter (mm)', part.diameter || ''],
        ['Length of Cut (mm)', part.loc || ''],
        ['Heat Treatment', part.heat_treatment || ''],
        ['Process Sequence', (part.process_sequence || []).join(' → ')],
        ['', ''],
        ['SOP Links', ''],
      ];
      
      // Add SOP links if available
      if (part.sop_links && Object.keys(part.sop_links).length > 0) {
        Object.entries(part.sop_links).forEach(([process, link]) => {
          partDetailsData.push([`  ${process}`, link || '']);
        });
      } else {
        partDetailsData.push(['  (No SOP links)', '']);
      }

      partDetailsData.push(['', '']);
      partDetailsData.push(['Drawings', '']);
      
      // Add drawings if available
      if (part.drawings && part.drawings.length > 0) {
        part.drawings.forEach((drawing, idx) => {
          partDetailsData.push([`  Drawing ${idx + 1}`, drawing.filename || drawing.name || `Drawing ${idx + 1}`]);
        });
      } else {
        partDetailsData.push(['  (No drawings uploaded)', '']);
      }

      const partSheet = XLSX.utils.aoa_to_sheet([['Field', 'Value'], ...partDetailsData]);
      partSheet['!cols'] = [{ wch: 30 }, { wch: 50 }];
      XLSX.utils.book_append_sheet(workbook, partSheet, 'Part Details');

      // ============ SHEET 5: PACKING LIST ============
      try {
        const pkgRes = await axios.get(`${BACKEND_URL}/api/works-orders/${order.order_number}/packages`);
        const pkgData = pkgRes.data;
        const packingRows = [
          ['Works Order', order.order_number],
          ['Customer', order.customer],
          ['Customer Order Number', order.customer_order_number || ''],
          ['Part Number', order.part_number],
          ['Cast Number', cuttingData.cast_number || ''],
          ['HT Certificate Number', heatTreatmentData.ht_certificate_number || ''],
          ['WO Quantity', pkgData.summary?.wo_quantity || order.quantity],
          ['Total Packed', pkgData.summary?.total_packed || 0],
          ['Balance Remaining', pkgData.summary?.balance_remaining ?? ''],
          ['Packing Status', pkgData.summary?.status === 'fully_packed' ? 'Fully Packed' : pkgData.summary?.status === 'partially_packed' ? 'Partially Packed' : 'Not Packed'],
          ['', ''],
        ];
        const headerRow = ['Package ID', 'Package Type', 'Quantity', 'Date Created', 'Created By', 'Notes'];
        const packageRows = (pkgData.packages || []).map(p => [
          p.package_id,
          p.package_type_display || p.package_type,
          p.quantity,
          p.created_at ? new Date(p.created_at).toLocaleDateString() : '',
          p.created_by || '',
          p.notes || ''
        ]);
        const packingSheet = XLSX.utils.aoa_to_sheet([...packingRows.map(r => [r[0], r[1]]), headerRow, ...packageRows]);
        packingSheet['!cols'] = [{ wch: 25 }, { wch: 20 }, { wch: 12 }, { wch: 15 }, { wch: 25 }, { wch: 30 }];
        XLSX.utils.book_append_sheet(workbook, packingSheet, 'Packing List');
      } catch (pkgErr) {
        console.warn('No packages found for order:', pkgErr);
      }

      // Write the file
      XLSX.writeFile(workbook, `${order.order_number}_Extract.xlsx`);
      toast.success(`Exported: ${order.order_number}_Extract.xlsx`);
    } catch (error) {
      console.error('Error extracting works order:', error);
      toast.error('Failed to extract works order data');
    } finally {
      setExtractingOrder(null);
    }
  };

  const handleGenerateCertificate = async (order) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/parts/${order.part_number}`);
      setCertificatePartDetails(response.data);
      setCertificateOrder(order);
      setShowCertificate(true);
    } catch (error) {
      console.error('Error fetching part details:', error);
      toast.error('Failed to load part details for certificate');
    }
  };

  // Production submenu handler
  const handleProductionAction = (action) => {
    setShowProductionSubmenu(false);
    switch (action) {
      case 'schedule':
        navigate('/kanban');
        break;
      case 'outstanding':
        fetchOutstandingOrders();
        break;
      case 'completed':
        fetchCompletedOrders();
        break;
      case 'extract':
        fetchExtractOrders();
        break;
      default:
        break;
    }
  };

  // Filter functions
  const getFilteredOutstandingOrders = () => {
    return outstandingOrders.filter(order => {
      if (outstandingFilterCustomer && order.customer !== outstandingFilterCustomer) return false;
      if (outstandingFilterPart && order.part_number !== outstandingFilterPart) return false;
      return true;
    });
  };

  const getFilteredCompletedOrders = () => {
    return completedOrders.filter(order => {
      if (completedFilterCustomer && order.customer !== completedFilterCustomer) return false;
      if (completedFilterPart && order.part_number !== completedFilterPart) return false;
      return true;
    });
  };

  const getFilteredExtractOrders = () => {
    if (!extractSearchQuery.trim()) return extractOrders;
    const query = extractSearchQuery.toLowerCase().trim();
    return extractOrders.filter(order => {
      const part = parts.find(p => p.part_number === order.part_number);
      const description = part?.description || '';
      return (
        order.order_number.toLowerCase().includes(query) ||
        order.part_number.toLowerCase().includes(query) ||
        order.customer.toLowerCase().includes(query) ||
        (order.customer_order_number || '').toLowerCase().includes(query) ||
        description.toLowerCase().includes(query)
      );
    });
  };

  const filteredOutstandingOrders = getFilteredOutstandingOrders();
  const filteredCompletedOrders = getFilteredCompletedOrders();
  const filteredExtractOrders = getFilteredExtractOrders();

  // Main navigation nodes configuration - clockwise from 12 o'clock
  const mainNodes = [
    { icon: FilePlus, label: 'New Order', angle: -90, action: () => navigate('/works-order/create'), testId: 'nav-new-order' },
    { icon: Wrench, label: 'Engineering', angle: -39, action: () => navigate('/engineering'), testId: 'nav-engineering' },
    { icon: Factory, label: 'Production', angle: 12, action: () => setShowProductionSubmenu(!showProductionSubmenu), badge: stats.lateOrders > 0 ? stats.lateOrders : null, badgeColor: 'orange', testId: 'nav-production', isExpandable: true },
    { icon: AlertTriangle, label: 'NCR', angle: 63, action: () => setShowNcrModule(true), badge: stats.openNcrs > 0 ? stats.openNcrs : null, badgeColor: 'red', testId: 'nav-ncr' },
    { icon: Gauge, label: 'Calibration', angle: 114, action: () => navigate('/calibration'), badge: stats.calibrationsDue > 0 ? stats.calibrationsDue : null, badgeColor: 'blue', testId: 'nav-calibration' },
    { icon: FileBadge2, label: 'Certificates', angle: 165, action: () => navigate('/certificates'), badge: stats.missingCerts > 0 ? stats.missingCerts : null, badgeColor: 'purple', testId: 'nav-certificates' },
    { icon: Package, label: 'Inventory', angle: 216, action: () => navigate('/inventory'), testId: 'nav-inventory' }
  ];

  const utilityNodes = [
    { icon: Database, label: 'Backup', action: handleBackupData, testId: 'nav-backup' },
    { icon: LayoutDashboard, label: 'Dashboard', action: () => navigate('/dashboard'), testId: 'nav-dashboard' },
    { icon: Building2, label: 'Customers', action: () => setShowCustomerManagement(true), testId: 'nav-customers' },
    { icon: Search, label: 'Order History', action: () => navigate('/customer-orders'), testId: 'nav-order-history' },
    { icon: ClipboardCheck, label: 'Actions', action: () => setShowActionItems(true), testId: 'nav-actions' },
    { icon: Shield, label: 'ISO9001', action: () => setShowIsoSubmenu(!showIsoSubmenu), testId: 'nav-iso', hasSubmenu: true },
    { icon: Wrench, label: 'Maintenance', action: () => setShowMaintenance(true), testId: 'nav-maintenance' },
    { icon: Users, label: 'Users', action: () => navigate('/admin/users'), testId: 'nav-users', adminOnly: true }
  ];

  // ISO9001:2015 Submenu items
  const isoSubmenuItems = [
    { icon: BookOpen, label: 'Quality Manual', action: () => { setShowQualityManual(true); setShowIsoSubmenu(false); }, testId: 'iso-quality-manual' },
    { icon: FileText, label: 'Policies', action: () => { setShowPolicyModule(true); setShowIsoSubmenu(false); }, testId: 'iso-policies' },
    { icon: ClipboardList, label: 'Procedures', action: () => { setShowProcedureModule(true); setShowIsoSubmenu(false); }, testId: 'iso-procedures' },
    { icon: AlertOctagon, label: 'Risk Manager', action: () => { setShowRiskManager(true); setShowIsoSubmenu(false); }, testId: 'iso-risk-manager' },
    { icon: Network, label: 'Organogram', action: () => { import('@/components/Organogram').then(m => { setOrgComp(() => m.default); setShowOrganogram(true); }); setShowIsoSubmenu(false); }, testId: 'iso-organogram' }
  ];

  return (
    <div className="min-h-screen bg-[#050505] overflow-hidden relative">
      {/* Status Bar */}
      <StatusBar stats={stats} />
      
      {/* Honeycomb mesh background */}
      <div className="absolute inset-0 overflow-hidden">
        <svg className="absolute inset-0 w-full h-full opacity-40" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="honeycomb" x="0" y="0" width="56" height="100" patternUnits="userSpaceOnUse" patternTransform="scale(1.5)">
              <path 
                d="M28 0 L56 16.67 L56 50 L28 66.67 L0 50 L0 16.67 Z M28 100 L56 83.33 L56 116.67 L28 133.33 L0 116.67 L0 83.33 Z M84 50 L112 66.67 L112 100 L84 116.67 L56 100 L56 66.67 Z M-28 50 L0 66.67 L0 100 L-28 116.67 L-56 100 L-56 66.67 Z" 
                fill="none" 
                stroke="rgba(0, 200, 255, 0.6)" 
                strokeWidth="1.5"
              />
            </pattern>
            <radialGradient id="honeycomb-fade" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
              <stop offset="0%" stopColor="white" stopOpacity="0" />
              <stop offset="35%" stopColor="white" stopOpacity="0" />
              <stop offset="60%" stopColor="white" stopOpacity="0.5" />
              <stop offset="100%" stopColor="white" stopOpacity="1" />
            </radialGradient>
            <mask id="honeycomb-mask">
              <rect width="100%" height="100%" fill="url(#honeycomb-fade)" />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="url(#honeycomb)" mask="url(#honeycomb-mask)" />
        </svg>
      </div>
      
      {/* Background radial glow */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `radial-gradient(circle at center, rgba(0, 240, 255, 0.15) 0%, transparent 50%)`,
          backgroundSize: '100% 100%'
        }}
      />

      {/* User controls - top right */}
      <div className="fixed top-14 right-4 md:right-6 z-40 flex items-center gap-2 md:gap-3">
        <div className="flex items-center gap-2 bg-slate-900/80 backdrop-blur-sm border border-cyan-500/20 rounded-full px-3 md:px-4 py-1.5 md:py-2">
          <div className="w-7 h-7 md:w-8 md:h-8 bg-cyan-500/20 rounded-full flex items-center justify-center">
            <span className="text-cyan-400 font-bold text-xs md:text-sm">{user?.name?.charAt(0).toUpperCase()}</span>
          </div>
          <span className="text-white text-xs md:text-sm font-medium hidden sm:inline">{user?.name}</span>
          {isAdmin() && (
            <span className="bg-cyan-500/20 text-cyan-400 text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 rounded-full flex items-center gap-1">
              <Shield className="w-3 h-3" />
              <span className="hidden sm:inline">Admin</span>
            </span>
          )}
        </div>
        <Button
          onClick={() => { logout(); navigate('/login'); toast.success('Logged out'); }}
          variant="ghost"
          size="sm"
          className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" />
        </Button>
      </div>

      {/* ===== MOBILE GRID NAVIGATION ===== */}
      <div className="md:hidden relative z-30 pt-24 pb-32 px-4 min-h-screen flex flex-col">
        {/* Mobile Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-black text-white uppercase tracking-tight">
            Hot<span className="text-cyan-400">forge</span>
          </h1>
          <p className="text-[10px] text-slate-500 uppercase tracking-widest">Factory Command Center</p>
        </div>

        {/* Mobile Grid - Main Nav */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          {mainNodes.map((node) => (
            <button
              key={node.testId}
              onClick={node.action}
              data-testid={`mobile-${node.testId}`}
              className="relative flex flex-col items-center gap-2 bg-slate-900/80 backdrop-blur-sm border border-cyan-500/15 rounded-xl p-4 hover:border-cyan-500/40 active:bg-cyan-500/10 transition-all"
            >
              {node.badge && (
                <span className={`absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold rounded-full text-white ${node.badgeColor === 'red' ? 'bg-red-500' : node.badgeColor === 'orange' ? 'bg-orange-500' : node.badgeColor === 'blue' ? 'bg-blue-500' : 'bg-purple-500'}`}>
                  {node.badge}
                </span>
              )}
              <node.icon className="w-6 h-6 text-cyan-400" />
              <span className="text-[10px] text-slate-300 uppercase tracking-wider font-bold text-center leading-tight">{node.label}</span>
            </button>
          ))}
        </div>

        {/* Mobile Grid - Utility Nav */}
        <div className="grid grid-cols-4 gap-2">
          {utilityNodes.map((node) => {
            if (node.adminOnly && !isAdmin()) return null;
            return (
              <button
                key={node.testId}
                onClick={node.action}
                data-testid={`mobile-${node.testId}`}
                className="flex flex-col items-center gap-1.5 bg-slate-900/60 border border-slate-800 rounded-lg p-3 hover:border-cyan-500/30 active:bg-cyan-500/10 transition-all"
              >
                <node.icon className="w-5 h-5 text-slate-400" />
                <span className="text-[9px] text-slate-500 uppercase tracking-wider font-medium text-center leading-tight">{node.label}</span>
              </button>
            );
          })}
        </div>

        {/* Mobile Production Submenu */}
        {showProductionSubmenu && (
          <div className="mt-3 bg-slate-900/95 backdrop-blur-xl border border-cyan-500/20 rounded-xl p-3 animate-in fade-in duration-200">
            <h4 className="text-xs text-cyan-400 font-bold uppercase tracking-wider mb-2 px-1">Production</h4>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Schedule', action: () => handleProductionAction('schedule'), icon: Calendar },
                { label: 'Outstanding', action: () => handleProductionAction('outstanding'), icon: AlertTriangle },
                { label: 'Completed', action: () => handleProductionAction('completed'), icon: CheckCircle2 },
                { label: 'Extract', action: () => handleProductionAction('extract'), icon: Download }
              ].map((item) => (
                <button key={item.label} onClick={item.action} className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-slate-800/50 hover:bg-cyan-500/10 text-sm text-slate-300">
                  <item.icon className="w-4 h-4 text-cyan-400" />
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Mobile ISO Submenu */}
        {showIsoSubmenu && (
          <div className="mt-3 bg-slate-900/95 backdrop-blur-xl border border-cyan-500/20 rounded-xl p-3 animate-in fade-in duration-200">
            <h4 className="text-xs text-cyan-400 font-bold uppercase tracking-wider mb-2 px-1">ISO 9001:2015</h4>
            <div className="grid grid-cols-2 gap-2">
              {isoSubmenuItems.map((item) => (
                <button key={item.testId} onClick={item.action} data-testid={`mobile-${item.testId}`} className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-slate-800/50 hover:bg-cyan-500/10 text-sm text-slate-300">
                  <item.icon className="w-4 h-4 text-cyan-400" />
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ===== DESKTOP ORBITAL INTERFACE ===== */}
      <div className="hidden md:flex items-center justify-center min-h-screen pt-12">
        <div className="relative" style={{ width: '800px', height: '800px' }}>
          
          {/* SVG Connection Lines */}
          <svg 
            className="absolute inset-0 w-full h-full"
            viewBox="-400 -400 800 800"
            style={{ overflow: 'visible' }}
          >
            {mainNodes.map((node) => (
              <ConnectionLine key={node.testId} angle={node.angle} distance={270} />
            ))}
          </svg>

          {/* Center Core */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <ReactorCore />
          </div>

          {/* Main Navigation Nodes */}
          {mainNodes.map((node) => (
            <OrbitalNode
              key={node.testId}
              icon={node.icon}
              label={node.label}
              angle={node.angle}
              distance={270}
              size="primary"
              badge={node.badge}
              badgeColor={node.badgeColor}
              onClick={node.action}
              isExpanded={node.isExpandable && showProductionSubmenu}
              testId={node.testId}
            />
          ))}

          {/* Production Submenu */}
          <ProductionSubmenu 
            isOpen={showProductionSubmenu}
            onNavigate={handleProductionAction}
            parentAngle={12}
            parentDistance={270}
          />
        </div>
      </div>

      {/* Right Side Vertical Utility Dock - DESKTOP ONLY */}
      <div className="hidden md:flex fixed right-6 top-1/2 -translate-y-1/2 z-40 items-center gap-2">
        {/* ISO9001:2015 Submenu - appears to the left */}
        {showIsoSubmenu && (
          <div className="bg-slate-900/95 backdrop-blur-xl border border-cyan-500/30 rounded-2xl px-3 py-3 animate-in slide-in-from-right-2 duration-200">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
              <Shield className="w-4 h-4 text-cyan-400" />
              <span className="text-xs text-cyan-400 font-bold uppercase tracking-wider">ISO9001:2015</span>
            </div>
            <div className="flex flex-col gap-1">
              {isoSubmenuItems.map((item) => (
                <button
                  key={item.testId}
                  onClick={item.action}
                  data-testid={item.testId}
                  className="group flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-cyan-500/10 transition-colors text-left w-full"
                >
                  <item.icon className="w-4 h-4 text-slate-400 group-hover:text-cyan-400 transition-colors" />
                  <span className="text-sm text-slate-300 group-hover:text-white whitespace-nowrap">
                    {item.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Main Utility Dock */}
        <div className="flex flex-col items-center gap-2 bg-slate-900/80 backdrop-blur-xl border border-cyan-500/20 rounded-2xl px-3 py-4">
          {utilityNodes.map((node) => {
            if (node.adminOnly && !isAdmin()) return null;
            const isIsoButton = node.testId === 'nav-iso';
            return (
              <button
                key={node.testId}
                onClick={node.action}
                disabled={loadingBackup && node.testId === 'nav-backup'}
                data-testid={node.testId}
                className={`group flex flex-col items-center gap-1 px-3 py-2.5 rounded-xl transition-colors disabled:opacity-50 w-full ${
                  isIsoButton && showIsoSubmenu 
                    ? 'bg-cyan-500/20 text-cyan-400' 
                    : 'hover:bg-cyan-500/10'
                }`}
                title={node.label}
              >
                <node.icon className={`w-5 h-5 transition-colors ${
                  isIsoButton && showIsoSubmenu 
                    ? 'text-cyan-400' 
                    : 'text-slate-400 group-hover:text-cyan-400'
                }`} />
                <span className={`text-[10px] uppercase tracking-wider font-medium ${
                  isIsoButton && showIsoSubmenu 
                    ? 'text-cyan-400' 
                    : 'text-slate-500 group-hover:text-slate-300'
                }`}>
                  {loadingBackup && node.testId === 'nav-backup' ? '...' : node.label}
                </span>
                {isIsoButton && (
                  <ChevronLeft className={`w-3 h-3 absolute -left-1 transition-transform ${showIsoSubmenu ? 'rotate-180 text-cyan-400' : 'text-slate-600'}`} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Title */}
      <div className="hidden md:block fixed bottom-8 left-8 z-40">
        <h1 className="text-2xl font-black text-white uppercase tracking-tight">
          Hot<span className="text-cyan-400">forge</span>
        </h1>
        <p className="text-xs text-slate-500 uppercase tracking-widest">Factory Command Center</p>
      </div>

      {/* Left Side - Most Advanced Orders Panel - DESKTOP ONLY */}
      <div className="hidden lg:block fixed left-6 top-1/2 -translate-y-1/2 z-40 w-72">
        <div className="bg-slate-900/80 backdrop-blur-xl border border-cyan-500/20 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-green-500/20 border border-green-500/30 flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4 text-green-400" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white uppercase tracking-wide">Next to Complete</h3>
              <p className="text-[10px] text-slate-500">Most advanced job cards</p>
            </div>
          </div>
          
          {mostAdvancedOrders.length > 0 ? (
            <div className="space-y-2">
              {mostAdvancedOrders.map((order, idx) => (
                <div 
                  key={order.order_number}
                  className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 hover:border-cyan-500/30 transition-colors cursor-pointer"
                  onClick={() => navigate('/schedule')}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-cyan-400 text-xs font-bold">{order.order_number}</span>
                    <span className="text-[10px] text-green-400 font-bold">{Math.round(order.progress_percent)}%</span>
                  </div>
                  <p className="text-white text-xs font-medium truncate" title={order.description}>
                    {order.description}
                  </p>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-[10px] text-slate-500 truncate max-w-[100px]" title={order.customer}>
                      {order.customer}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-blue-950 text-blue-400 rounded">
                      {order.current_process}
                    </span>
                  </div>
                  {/* Progress Bar */}
                  <div className="mt-2 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 to-cyan-400 rounded-full transition-all"
                      style={{ width: `${order.progress_percent}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1 text-[9px] text-slate-500">
                    <span>{order.completed_steps}/{order.total_steps} steps</span>
                    <span>Qty: {order.quantity}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-slate-500 text-sm">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>No active orders</p>
            </div>
          )}
        </div>
      </div>

      {/* ============ DIALOGS ============ */}

      {/* NCR Module Dialog */}
      <Dialog open={showNcrModule} onOpenChange={setShowNcrModule}>
        <DialogContent className="bg-slate-950 border-cyan-500/30 max-w-6xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight text-xl flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-red-400" />
              Non-Conformance Reports
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[75vh]">
            <NcrKanbanBoard onNcrChange={setNcrs} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Outstanding Orders Dialog */}
      <Dialog open={showOutstandingOrders} onOpenChange={setShowOutstandingOrders}>
        <DialogContent className="bg-slate-950 border-cyan-500/30 max-w-5xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-white uppercase tracking-tight text-xl">
                  Outstanding Orders ({filteredOutstandingOrders.length})
                </DialogTitle>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-slate-400 text-sm">WIP Value:</span>
                  <span className="text-cyan-400 font-bold text-lg">
                    R {filteredOutstandingOrders.reduce((total, order) => total + ((order.quantity || 0) * (order.unit_price || 0)), 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>
          </DialogHeader>

          {/* Filters */}
          <div className="grid grid-cols-3 gap-4 py-4 border-b border-slate-800">
            <div>
              <Label className="text-slate-400 uppercase text-xs mb-2 block">Customer</Label>
              <Select value={outstandingFilterCustomer || "all"} onValueChange={(val) => setOutstandingFilterCustomer(val === "all" ? "" : val)}>
                <SelectTrigger className="bg-slate-900 border-slate-700">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {uniqueCustomers.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-400 uppercase text-xs mb-2 block">Part</Label>
              <Select value={outstandingFilterPart || "all"} onValueChange={(val) => setOutstandingFilterPart(val === "all" ? "" : val)}>
                <SelectTrigger className="bg-slate-900 border-slate-700">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {uniqueParts.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={() => { setOutstandingFilterCustomer(''); setOutstandingFilterPart(''); }} className="w-full border-slate-700">
                Clear
              </Button>
            </div>
          </div>

          <div className="overflow-y-auto max-h-[55vh] space-y-3 mt-4">
            {filteredOutstandingOrders.map((order) => {
              let completedSteps = 0;
              for (let i = 0; i < order.process_sequence.length; i++) {
                if (getOrderProcessStatus(order, i, order.process_sequence[i]) === 'Complete') completedSteps++;
              }
              const totalSteps = order.process_sequence.length;
              const progress = Math.round((completedSteps / totalSteps) * 100);
              const { process: currentProcess, status: currentStatus } = getCurrentProcessInfo(order);

              return (
                <div
                  key={order.order_number}
                  className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 hover:border-cyan-500/50 transition-colors"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div 
                      className="cursor-pointer flex-1"
                      onClick={() => { setShowOutstandingOrders(false); navigate('/schedule', { state: { searchOrder: order.order_number } }); }}
                    >
                      <h4 className="font-mono text-white font-bold">{order.order_number}</h4>
                      <p className="text-slate-400 text-sm">{order.customer} - {order.part_number}</p>
                      {order.customer_order_number && (
                        <p className="text-amber-400/80 text-xs font-mono mt-0.5">PO: {order.customer_order_number}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          setShowOutstandingOrders(false); 
                          navigate(`/works-order/edit/${order.order_number}`); 
                        }}
                        className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 h-8 w-8 p-0"
                        data-testid={`edit-order-${order.order_number}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <span className={`text-xs px-2 py-1 rounded font-bold ${currentStatus === 'In Progress' ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'}`}>
                        {currentStatus}
                      </span>
                    </div>
                  </div>
                  <div 
                    className="cursor-pointer"
                    onClick={() => { setShowOutstandingOrders(false); navigate('/schedule', { state: { searchOrder: order.order_number } }); }}
                  >
                    <div className="flex justify-between items-center text-sm mb-2">
                      <span className="text-slate-500">Current: <span className="text-white">{currentProcess}</span></span>
                      <span className="text-cyan-400">{progress}%</span>
                    </div>
                    <div className="w-full bg-slate-800 rounded-full h-1.5">
                      <div className="bg-cyan-500 h-1.5 rounded-full" style={{ width: `${progress}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Completed Orders Dialog */}
      <Dialog open={showCompletedOrders} onOpenChange={setShowCompletedOrders}>
        <DialogContent className="bg-slate-950 border-cyan-500/30 max-w-5xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight text-xl flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-green-400" />
              Completed Orders ({filteredCompletedOrders.length})
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-3 gap-4 py-4 border-b border-slate-800">
            <div>
              <Label className="text-slate-400 uppercase text-xs mb-2 block">Customer</Label>
              <Select value={completedFilterCustomer || "all"} onValueChange={(val) => setCompletedFilterCustomer(val === "all" ? "" : val)}>
                <SelectTrigger className="bg-slate-900 border-slate-700">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {completedUniqueCustomers.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-400 uppercase text-xs mb-2 block">Part</Label>
              <Select value={completedFilterPart || "all"} onValueChange={(val) => setCompletedFilterPart(val === "all" ? "" : val)}>
                <SelectTrigger className="bg-slate-900 border-slate-700">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {completedUniqueParts.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={() => { setCompletedFilterCustomer(''); setCompletedFilterPart(''); }} className="w-full border-slate-700">
                Clear
              </Button>
            </div>
          </div>

          <div className="overflow-y-auto max-h-[55vh] space-y-3 mt-4">
            {filteredCompletedOrders.map((order) => {
              const createdAt = order.created_at ? new Date(order.created_at) : null;
              const completedAt = order.completed_at ? new Date(order.completed_at) : null;
              const dueDate = order.due_date ? new Date(order.due_date) : null;
              const deliveryDays = createdAt && completedAt ? Math.round((completedAt - createdAt) / (1000 * 60 * 60 * 24)) : null;
              const isOnTime = completedAt && dueDate ? completedAt <= dueDate : null;
              const daysLate = !isOnTime && completedAt && dueDate ? Math.round((completedAt - dueDate) / (1000 * 60 * 60 * 24)) : 0;

              // Check for incomplete steps on closed orders
              const incompleteSteps = [];
              if (order.current_status === 'Closed') {
                (order.process_sequence || []).forEach((step, idx) => {
                  const status = order.process_statuses?.[String(idx)] || order.process_statuses?.[step] || 'Not Ready';
                  if (status !== 'Complete') {
                    incompleteSteps.push({ index: idx, name: step, status });
                  }
                });
              }

              return (
              <div
                key={order.order_number}
                className={`rounded-lg p-4 hover:border-green-500/50 transition-colors ${
                  incompleteSteps.length > 0
                    ? 'bg-amber-950/20 border border-amber-500/30'
                    : 'bg-green-950/20 border border-green-800/30'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div 
                    className="cursor-pointer flex-1"
                    onClick={() => { setShowCompletedOrders(false); navigate('/schedule', { state: { searchOrder: order.order_number } }); }}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-mono text-white font-bold">{order.order_number}</h4>
                      <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">Complete</span>
                      {isOnTime === true && <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-300">On-Time</span>}
                      {isOnTime === false && <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400">Late ({daysLate}d)</span>}
                      {incompleteSteps.length > 0 && (
                        <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 font-bold" data-testid={`incomplete-badge-${order.order_number}`}>
                          {incompleteSteps.length} Incomplete Step{incompleteSteps.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <p className="text-slate-400 text-sm mt-1">{order.customer} - {order.part_number}</p>
                    {order.customer_order_number && (
                      <p className="text-amber-400/80 text-xs font-mono mt-0.5">PO: {order.customer_order_number}</p>
                    )}
                    <div className="flex gap-4 mt-2 text-xs text-slate-500">
                      <span>Created: {createdAt ? createdAt.toLocaleDateString() : '-'}</span>
                      <span>Delivered: {completedAt ? completedAt.toLocaleDateString() : '-'}</span>
                      {deliveryDays !== null && <span className="text-green-400 font-mono font-bold">Delivery: {deliveryDays} days</span>}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap justify-end">
                    {incompleteSteps.length > 0 && isAdmin() && (
                      <Button
                        size="sm"
                        className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/50 font-bold uppercase tracking-wide text-xs"
                        data-testid={`finalize-btn-${order.order_number}`}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm(`Finalize ${incompleteSteps.length} incomplete step(s) on ${order.order_number}?\n\nSteps: ${incompleteSteps.map(s => s.name).join(', ')}\n\nThis will mark them as Complete and update the customer outstanding records.`)) return;
                          try {
                            for (const step of incompleteSteps) {
                              await axios.post(`${BACKEND_URL}/api/works-orders/${order.order_number}/finalize-step`, {
                                step_index: step.index,
                                admin_email: user.email,
                                notes: `Retrospective finalization of ${step.name} after order closure`
                              });
                            }
                            toast.success(`${order.order_number}: ${incompleteSteps.map(s => s.name).join(', ')} finalized`);
                            fetchCompletedOrders();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to finalize step');
                          }
                        }}
                      >
                        Finalize Steps
                      </Button>
                    )}
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 h-8 w-8 p-0" 
                      onClick={(e) => { e.stopPropagation(); setShowCompletedOrders(false); navigate(`/works-order/edit/${order.order_number}`); }}
                      data-testid={`edit-order-${order.order_number}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10" onClick={(e) => { e.stopPropagation(); handleGenerateCertificate(order); }}>
                      <Award className="w-4 h-4 mr-1" /> Certificate
                    </Button>
                  </div>
                </div>
                {/* Show incomplete steps detail */}
                {incompleteSteps.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-amber-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-xs text-amber-400 font-bold uppercase tracking-wider">Incomplete Steps (Post-Closure)</span>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {incompleteSteps.map(s => (
                        <span key={s.index} className="text-xs px-2 py-1 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20 font-mono">
                          {s.name}: {s.status}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Extract Dialog */}
      <Dialog open={showExtractDialog} onOpenChange={setShowExtractDialog}>
        <DialogContent className="bg-slate-950 border-cyan-500/30 max-w-4xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight text-xl flex items-center gap-2">
              <FileSpreadsheet className="w-6 h-6 text-purple-400" />
              Extract Works Order Data
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 border-b border-slate-800">
            <Input
              value={extractSearchQuery}
              onChange={(e) => setExtractSearchQuery(e.target.value)}
              placeholder="Search by order, customer, or part..."
              className="bg-slate-900 border-slate-700"
            />
          </div>

          <div className="overflow-y-auto max-h-[55vh] space-y-3 mt-4">
            {filteredExtractOrders.map((order) => (
              <div key={order.order_number} className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 flex justify-between items-center">
                <div>
                  <h4 className="font-mono text-white font-bold">{order.order_number}</h4>
                  <p className="text-slate-400 text-sm">{order.customer} - {order.part_number}</p>
                </div>
                <Button
                  onClick={() => handleExtractWorksOrder(order)}
                  disabled={extractingOrder === order.order_number}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {extractingOrder === order.order_number ? 'Extracting...' : 'Extract'}
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Certificate Dialog */}
      {showCertificate && certificateOrder && certificatePartDetails && (
        <CertificateOfConformance
          order={certificateOrder}
          part={certificatePartDetails}
          onClose={() => { setShowCertificate(false); setCertificateOrder(null); setCertificatePartDetails(null); }}
        />
      )}

      {/* Policy Module */}
      {showPolicyModule && (
        <div className="fixed inset-0 z-[100] bg-slate-950">
          <PolicyManager onClose={() => setShowPolicyModule(false)} />
        </div>
      )}

      {/* Procedure Module */}
      {showProcedureModule && (
        <div className="fixed inset-0 z-[100] bg-slate-950">
          <ProcedureManager onClose={() => setShowProcedureModule(false)} />
        </div>
      )}

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes dash {
          to {
            stroke-dashoffset: -100;
          }
        }
      `}</style>

      {/* Customer Management Full-Screen Module */}
      {showCustomerManagement && (
        <div className="fixed inset-0 z-[100]">
          <CustomerManagement onClose={() => setShowCustomerManagement(false)} />
        </div>
      )}

      {/* Action Items Panel */}
      <ActionItemsPanel isOpen={showActionItems} onClose={() => setShowActionItems(false)} />

      {/* Quality Manual Dialog */}
      <Dialog open={showQualityManual} onOpenChange={setShowQualityManual}>
        <DialogContent className="bg-slate-900 border-cyan-500/30 max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-wide flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-cyan-400" />
              Quality Manual
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-12 text-slate-500">
            <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg">Quality Manual Module</p>
            <p className="text-sm mt-2">ISO9001:2015 Quality Management System Documentation</p>
            <p className="text-xs mt-4 text-slate-600">Coming soon - Upload and manage your Quality Manual here</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Risk Manager */}
      <RiskManager isOpen={showRiskManager} onClose={() => setShowRiskManager(false)} />

      {/* Organogram */}
      {showOrganogram && OrgComp && <OrgComp isOpen={showOrganogram} onClose={() => setShowOrganogram(false)} />}

      {/* Maintenance Module */}
      {showMaintenance && (
        <Dialog open={showMaintenance} onOpenChange={setShowMaintenance}>
          <DialogContent className="bg-slate-950 border-amber-500/20 max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="maintenance-module-dialog">
            <MaintenanceModule onClose={() => setShowMaintenance(false)} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Landing;
